import { createContext, useContext, useState, useEffect } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

    const initAuth = async () => {
      if (token) {
        // Only an explicit auth rejection ends the session. Transient failures
        // (429 rate limit, network error, backend restarting) are retried.
        for (let attempt = 0; attempt < 4 && !cancelled; attempt += 1) {
          try {
            const response = await api.get("/auth/me");
            if (!cancelled) setUser(response.data);
            break;
          } catch (error) {
            const status = error?.response?.status;
            if (status === 401 || status === 403) {
              console.error("Auth init error:", error);
              if (!cancelled) logout();
              break;
            }
            if (attempt === 3) {
              console.error("Auth init error (giving up):", error);
              break;
            }
            const retryAfter = Number(error?.response?.headers?.["retry-after"]);
            const delay = Number.isFinite(retryAfter) && retryAfter > 0
              ? Math.min(retryAfter, 10) * 1000
              : 1000 * (attempt + 1);
            await sleep(delay);
          }
        }
      }
      if (!cancelled) setLoading(false);
    };

    initAuth();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const startSession = (newToken, userData) => {
    localStorage.setItem("token", newToken);
    setToken(newToken);
    setUser(userData);
  };

  /**
   * Step 1: e-mail + password. When 2-step verification is on, the backend
   * e-mails a 6-digit code and returns `{ two_factor_required, challenge_id, ... }`
   * instead of a token — in that case this resolves to `{ twoFactor: {...} }`.
   * Otherwise the session starts immediately and the user object is returned.
   */
  const login = async (email, password, turnstileToken = null) => {
    const payload = { email, password };
    if (turnstileToken) {
      payload.turnstile_token = turnstileToken;
    }
    const response = await api.post("/auth/login", payload);
    if (response.data?.two_factor_required) {
      return { twoFactor: response.data };
    }
    const { token: newToken, user: userData } = response.data;
    startSession(newToken, userData);
    return userData;
  };

  /** Step 2: exchange the e-mailed code for the session token. */
  const verifyLogin = async (challengeId, code) => {
    const response = await api.post("/auth/login/verify", { challenge_id: challengeId, code });
    const { token: newToken, user: userData } = response.data;
    startSession(newToken, userData);
    return userData;
  };

  const resendLoginCode = async (challengeId) => {
    const response = await api.post("/auth/login/resend", { challenge_id: challengeId });
    return response.data;
  };

  const register = async (inviteCode, userData) => {
    const response = await api.post(`/auth/register/${inviteCode}`, userData);
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    setToken(null);
    setUser(null);
    // Force redirect to login
    window.location.href = "/login";
  };

  const changePassword = async (currentPassword, newPassword) => {
    await api.post("/auth/change-password", {
      current_password: currentPassword,
      new_password: newPassword,
    });
    
    // Update user state
    setUser((prev) => ({ ...prev, force_password_change: false }));
  };

  const refreshUser = async () => {
    try {
      const response = await api.get("/auth/me");
      setUser(response.data);
      return response.data;
    } catch (error) {
      console.error("Refresh user error:", error);
      return null;
    }
  };

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!token && !!user,
    login,
    verifyLogin,
    resendLoginCode,
    register,
    logout,
    changePassword,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
